import { useId, type ReactNode } from "react";

import { cn } from "../../lib/cn";

export interface SparklineProps {
  /** Série de valeurs, de la plus ancienne à la plus récente. */
  data: number[];
  className?: string;
  /** Remplit la surface sous la courbe. À réserver aux volumes cumulables. */
  area?: boolean;
  "aria-label"?: string;
}

/**
 * Courbe de tendance miniature.
 *
 * ── Deux choix qui ne se voient pas mais changent tout ───────────────────────
 *
 * `preserveAspectRatio="none"` : la courbe s'étire pour occuper exactement la
 * largeur disponible, quelle que soit la colonne. Le prix serait normalement
 * une épaisseur de trait déformée avec l'étirement ; `vector-effect:
 * non-scaling-stroke` l'annule, et le trait reste d'épaisseur constante.
 * Sans ce couple, une sparkline dans une colonne étroite affiche un trait
 * gras et écrasé, et fin dans une colonne large.
 *
 * L'échelle verticale est calée sur le MIN et le MAX de la série, pas sur zéro.
 * Une occupation mémoire qui oscille entre 71 % et 73 % est une droite plate si
 * l'axe part de zéro — donc une information perdue. Une sparkline ne sert pas à
 * lire des valeurs (le chiffre est juste à côté), elle sert à voir la FORME.
 */
export function Sparkline({ data, className, area = false, ...props }: SparklineProps) {
  const gradientId = useId();

  if (data.length < 2) {
    return <div className={cn("h-8 rounded bg-surface-sunken", className)} aria-hidden="true" />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  // Série constante : le dénominateur serait nul. On la trace à mi-hauteur.
  const span = max - min || 1;

  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 28 - ((value - min) / span) * 24;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const line = `M${points.join(" L")}`;

  return (
    <svg
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      className={cn("h-8 w-full overflow-visible", className)}
      role="img"
      {...props}
    >
      {area && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${line} L100,32 L0,32 Z`} fill={`url(#${gradientId})`} stroke="none" />
        </>
      )}

      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Le dernier point est marqué : c'est la valeur « maintenant », la seule
          que l'opérateur cherche vraiment sur une courbe de supervision. */}
      <circle
        cx={100}
        cy={28 - ((data[data.length - 1]! - min) / span) * 24}
        r="2"
        fill="currentColor"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export interface MetricCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Unité affichée en petit après la valeur (« ms », « req/s », « Gio »). */
  unit?: string;
  /** Variation sur la période, en pourcentage. Signe compris. */
  delta?: number;
  /**
   * Sens « souhaitable » de la variation.
   *
   * Sans ce réglage, une console peint toute hausse en vert — et affiche donc
   * une latence p99 qui double en vert, ce qui est exactement l'inverse du
   * message. Le sens dépend de la métrique, pas du signe.
   */
  deltaGoodWhen?: "up" | "down";
  series?: number[];
  /** Période couverte, affichée en pied de carte (« 24 dernières heures »). */
  period?: ReactNode;
  className?: string;
}

/**
 * Indicateur de supervision : une valeur, sa tendance, sa forme.
 *
 * Les trois sont indissociables. « 412 ms » ne dit rien ; « 412 ms, +38 % sur
 * 24 h, avec une marche il y a deux heures » désigne un déploiement.
 */
export function MetricCard({
  label,
  value,
  unit,
  delta,
  deltaGoodWhen = "up",
  series,
  period,
  className,
}: MetricCardProps) {
  const hasDelta = typeof delta === "number" && Number.isFinite(delta);
  const isGood = hasDelta && (delta > 0 ? deltaGoodWhen === "up" : deltaGoodWhen === "down");
  const isFlat = hasDelta && delta === 0;

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border border-border bg-surface-raised p-4",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-medium tracking-wide text-fg-muted uppercase">{label}</span>

        {hasDelta && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums",
              isFlat && "bg-surface-sunken text-fg-muted",
              !isFlat && isGood && "bg-success-subtle text-success-fg",
              !isFlat && !isGood && "bg-danger-subtle text-danger-fg",
            )}
          >
            {!isFlat && (
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className={cn("size-3", delta < 0 && "rotate-180")}
              >
                <path d="M10 15V5M6 9l4-4 4 4" />
              </svg>
            )}
            {delta > 0 ? "+" : ""}
            {delta}%
          </span>
        )}
      </div>

      {/* `tabular-nums` : sur un tableau de bord qui se rafraîchit, des chiffres
          à chasse variable font danser la valeur à chaque mise à jour. */}
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-semibold tracking-tight text-fg tabular-nums">{value}</span>
        {unit && <span className="text-sm text-fg-muted">{unit}</span>}
      </div>

      {series && series.length > 1 && (
        <Sparkline
          data={series}
          area
          className={cn(isFlat || !hasDelta ? "text-fg-muted" : isGood ? "text-success-fg" : "text-danger-fg")}
          aria-label={`Tendance de ${typeof label === "string" ? label : "la métrique"}`}
        />
      )}

      {period && <span className="text-xs text-fg-subtle">{period}</span>}
    </div>
  );
}
